import type { SvgIconComponent } from '@mui/icons-material'
import type { ListProps } from '@mui/material'
import { List, ListSubheader } from '@mui/material'
import type { ReactNode } from 'react'
import { matchPath } from 'react-router-dom'
import NavItem from './NavItem'

interface Item {
  path?: string
  icon?: SvgIconComponent
  activeIcon?: SvgIconComponent
  info?: ReactNode
  children?: Item[]
  title?: string
  disabled?: boolean
}

export interface NavSectionProps extends ListProps {
  items: Item[]
  pathname?: string
  title?: string
}

const renderNavItems = ({
  depth = 0,
  items,
  pathname,
}: {
  items: Item[]
  pathname: string
  depth?: number
}): ReactNode => (
  <List disablePadding>
    {items.reduce(
      (acc, item) =>
        // @ts-ignore
        reduceChildRoutes({
          acc,
          item,
          pathname,
          depth,
        }),
      []
    )}
  </List>
)

const matchesPath = (
  path: string | undefined,
  pathname: string,
  end: boolean
): boolean => !!path && !!matchPath({ path, end }, pathname)

const isItemActive = (item: Item, pathname: string): boolean =>
  item.children
    ? matchesPath(item.path, pathname, false) ||
      item.children.some((child) => isItemActive(child, pathname))
    : matchesPath(item.path, pathname, true)

const reduceChildRoutes = ({
  acc,
  pathname,
  item,
  depth,
}: {
  acc: ReactNode[]
  pathname: string
  item: Item
  depth: number
}): Array<ReactNode> => {
  const key = `${item.path}-${depth}`
  const isActive = isItemActive(item, pathname)

  if (item.children) {
    acc.push(
      <NavItem
        active={isActive}
        activeIcon={item.activeIcon}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={isActive}
        path={item.path}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          items: item.children,
          pathname,
        })}
      </NavItem>
    )
  } else {
    acc.push(
      <NavItem
        active={isActive}
        activeIcon={item.activeIcon}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        path={item.path}
        title={item.title}
      />
    )
  }

  return acc
}

const NavSection = (props: NavSectionProps) => {
  const { items: _items, pathname = '', title, ...other } = props

  const items = _items.filter((item) => !item.disabled)

  return (
    <List
      disablePadding
      subheader={
        !!title && (
          <ListSubheader
            disableGutters
            disableSticky
            sx={{
              color: 'text.primary',
              lineHeight: 2.5,
              fontWeight: 700,
            }}
          >
            {title}
          </ListSubheader>
        )
      }
      {...other}
    >
      {renderNavItems({
        items,
        pathname,
      })}
    </List>
  )
}

export default NavSection
